<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Visit Details</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Active household visit details and service recording" name="description" />
    <meta content="CBHI System" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Active Visit</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">HEW</a></li>
                    <li class="breadcrumb-item"><a href="/hew/visits">Visits</a></li>
                    <li class="breadcrumb-item active">Visit Details</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            <i class="bx bx-check-circle me-2"></i>
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            <i class="bx bx-info-circle me-2"></i>
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            <i class="bx bx-error-circle me-2"></i>
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading visit details...</p>
          </div>

          <!-- Visit Content -->
          <div v-else-if="visitData">
            <!-- Visit Header -->
            <div class="row">
              <div class="col-12">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <h4 class="text-white mb-2">{{ visitData.household.household_number }}</h4>
                        <p class="mb-0">
                          <i class="bx bx-user me-1"></i> Head: {{ visitData.household.household_head_name || 'Not assigned' }}<br>
                          <i class="bx bx-map me-1"></i> {{ visitData.household.kebele_name }}, {{ visitData.household.woreda_name }}<br>
                          <i class="bx bx-time-five me-1"></i> Started: {{ formatDateTime(visitData.visit.visit_date) }}
                          <span v-if="visitData.visit.visit_duration" class="ms-2">
                            • Duration: {{ visitData.visit.visit_duration }} minutes
                          </span>
                        </p>
                      </div>
                      <div class="col-md-4 text-md-end">
                        <div class="btn-group" role="group">
                          <button v-if="visitData.visit.status === 'ongoing'"
                                  @click="completeVisit" class="btn btn-success">
                            <i class="bx bx-check-circle"></i> Complete Visit
                          </button>
                          <button v-else class="btn btn-light" disabled>
                            <i class="bx bx-check"></i> Completed
                          </button>
                          <button @click="$router.push('/hew/households/' + visitData.household.household_id)"
                                  class="btn btn-light ms-2">
                            <i class="bx bx-home"></i> View Household
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="row">
              <div class="col-md-3 col-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Total Members</p>
                        <h4 class="mb-0">{{ visitData.members.length }}</h4>
                      </div>
                      <div class="avatar-sm align-self-center">
                        <span class="avatar-title rounded-circle bg-primary">
                          <i class="bx bx-group font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Services Given</p>
                        <h4 class="mb-0">{{ recordedServices.length }}</h4>
                      </div>
                      <div class="avatar-sm align-self-center">
                        <span class="avatar-title rounded-circle bg-success">
                          <i class="bx bx-check-square font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Pregnant Women</p>
                        <h4 class="mb-0">{{ pregnantWomen.length }}</h4>
                      </div>
                      <div class="avatar-sm align-self-center">
                        <span class="avatar-title rounded-circle bg-warning">
                          <i class="bx bx-female font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-3 col-6">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">Under 5 Children</p>
                        <h4 class="mb-0">{{ underFiveChildren.length }}</h4>
                      </div>
                      <div class="avatar-sm align-self-center">
                        <span class="avatar-title rounded-circle bg-info">
                          <i class="bx bx-child font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Service Recording Section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white">
                    <div class="d-flex justify-content-between align-items-center">
                      <h5 class="mb-0">Service Recording</h5>
                      <div>
                        <!-- Quick Service Buttons -->
                        <button @click="openServiceModal('health_education')"
                                class="btn btn-sm btn-outline-primary me-2">
                          <i class="bx bx-book-reader"></i> Health Education
                        </button>
                        <button @click="openServiceModal('environmental')"
                                class="btn btn-sm btn-outline-warning me-2">
                          <i class="bx bx-home-smile"></i> Environmental
                        </button>
                        <button @click="openServiceModal('referral')"
                                class="btn btn-sm btn-outline-danger">
                          <i class="bx bx-transfer"></i> Referral
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <!-- Members List with Service Options -->
                    <div class="table-responsive">
                      <table class="table align-middle table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Member</th>
                          <th>Age/Gender</th>
                          <th>CBHI Status</th>
                          <th>Health Status</th>
                          <th>Services Today</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="member in visitData.members" :key="member.member_id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-2">
                                <div class="avatar-title bg-primary text-white rounded-circle">
                                  {{ getInitials(member.first_name, member.surname) }}
                                </div>
                              </div>
                              <div>
                                <h6 class="mb-0">{{ member.first_name }} {{ member.surname }}</h6>
                                <small class="text-muted">{{ member.relationship_to_head }}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {{ getAge(member.date_of_birth) }}<br>
                            <small class="text-muted">{{ member.gender }}</small>
                          </td>
                          <td>
                              <span v-if="member.enrollment_type === 'cbhi_member'"
                                    class="badge bg-success">CBHI Active</span>
                            <span v-else class="badge bg-secondary">Non-CBHI</span>
                          </td>
                          <td>
                            <div v-if="member.chronic_conditions" class="mb-1">
                                <span class="badge bg-danger">
                                  <i class="bx bx-plus-medical"></i> Chronic
                                </span>
                            </div>
                            <div v-if="isPregnant(member)" class="mb-1">
                                <span class="badge bg-warning">
                                  <i class="bx bx-female"></i> Pregnant
                                </span>
                            </div>
                            <div v-if="member.vaccination_count < getRequiredVaccinations(member)" class="mb-1">
                                <span class="badge bg-info">
                                  <i class="bx bx-injection"></i> Vaccines Due
                                </span>
                            </div>
                            <span v-if="!member.chronic_conditions && !isPregnant(member)"
                                  class="text-success">
                                <i class="bx bx-check-circle"></i> Healthy
                              </span>
                          </td>
                          <td>
                            <div v-if="getMemberServices(member.member_id).length > 0">
                                <span v-for="service in getMemberServices(member.member_id)"
                                      :key="service.service_id"
                                      class="badge bg-success me-1 mb-1">
                                  {{ formatServiceType(service.service_type) }}
                                </span>
                            </div>
                            <span v-else class="text-muted">No services yet</span>
                          </td>
                          <td class="text-center">
                            <div class="dropdown">
                              <button class="btn btn-sm btn-primary dropdown-toggle"
                                      type="button" data-bs-toggle="dropdown">
                                <i class="bx bx-plus"></i> Add Service
                              </button>
                              <ul class="dropdown-menu">
                                <!-- Child Services -->
                                <li v-if="isChild(member)">
                                  <h6 class="dropdown-header">Child Services</h6>
                                </li>
                                <li v-if="isChild(member)">
                                  <a class="dropdown-item" @click="recordService(member, 'immunization')">
                                    <i class="bx bx-injection text-primary me-2"></i> Immunization
                                  </a>
                                </li>
                                <li v-if="isChild(member)">
                                  <a class="dropdown-item" @click="recordService(member, 'growth_monitoring')">
                                    <i class="bx bx-trending-up text-success me-2"></i> Growth Monitoring
                                  </a>
                                </li>
                                <li v-if="isChild(member)">
                                  <a class="dropdown-item" @click="recordService(member, 'vitamin_a')">
                                    <i class="bx bx-capsule text-warning me-2"></i> Vitamin A
                                  </a>
                                </li>

                                <!-- Maternal Services -->
                                <li v-if="isPregnant(member)">
                                  <h6 class="dropdown-header">Maternal Services</h6>
                                </li>
                                <li v-if="isPregnant(member)">
                                  <a class="dropdown-item" @click="recordService(member, 'anc_visit')">
                                    <i class="bx bx-female text-danger me-2"></i> ANC Visit
                                  </a>
                                </li>

                                <!-- General Services -->
                                <li><hr class="dropdown-divider"></li>
                                <li>
                                  <h6 class="dropdown-header">General Services</h6>
                                </li>
                                <li>
                                  <a class="dropdown-item" @click="recordService(member, 'health_education')">
                                    <i class="bx bx-book-reader text-info me-2"></i> Health Education
                                  </a>
                                </li>
                                <li>
                                  <a class="dropdown-item" @click="recordService(member, 'counseling')">
                                    <i class="bx bx-conversation text-primary me-2"></i> Counseling
                                  </a>
                                </li>
                                <li>
                                  <a class="dropdown-item" @click="recordService(member, 'first_aid')">
                                    <i class="bx bx-first-aid text-danger me-2"></i> First Aid
                                  </a>
                                </li>
                                <li>
                                  <a class="dropdown-item" @click="recordService(member, 'referral')">
                                    <i class="bx bx-transfer text-warning me-2"></i> Referral
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Recorded Services -->
                    <div v-if="recordedServices.length > 0" class="mt-4">
                      <h6 class="mb-3">Services Provided During This Visit</h6>
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                          <tr>
                            <th>Service</th>
                            <th>Member</th>
                            <th>Category</th>
                            <th>Details</th>
                            <th>Time</th>
                            <th></th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="service in recordedServices" :key="service.service_id">
                            <td>
                              <span class="badge" :class="getServiceBadgeClass(service.service_category)">
                                {{ formatServiceType(service.service_type) }}
                              </span>
                            </td>
                            <td>{{ service.member_name || 'Household' }}</td>
                            <td>
                              <span class="text-capitalize">{{ service.service_category }}</span>
                            </td>
                            <td>
                              <div class="service-details">
                                {{ formatServiceDetails(service.service_details) }}
                              </div>
                            </td>
                            <td>{{ formatTime(service.created_at) }}</td>
                            <td>
                              <button @click="removeService(service)"
                                      class="btn btn-sm btn-danger"
                                      v-if="visitData.visit.status === 'ongoing'">
                                <i class="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Visit Notes -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-white">
                    <h5 class="mb-0">Visit Notes</h5>
                  </div>
                  <div class="card-body">
                    <textarea v-model="visitNotes" class="form-control" rows="4"
                              placeholder="Add any additional notes about this visit..."
                              :disabled="visitData.visit.status === 'completed'"></textarea>
                    <button v-if="visitData.visit.status === 'ongoing'"
                            @click="saveNotes"
                            class="btn btn-sm btn-primary mt-2">
                      <i class="bx bx-save"></i> Save Notes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Data State -->
          <div v-else class="text-center py-5">
            <i class="bx bx-error-circle text-danger" style="font-size: 48px;"></i>
            <p class="mt-3 text-muted">Visit not found</p>
            <button @click="$router.push('/hew/dashboard')" class="btn btn-primary">
              Back to Dashboard
            </button>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Import Modal Components -->
      <ServiceRecordingModal
          v-if="showServiceModal"
          :member="selectedMember"
          :serviceType="selectedServiceType"
          :visitId="visitId"
          @close="closeServiceModal"
          @recorded="onServiceRecorded"
      />

      <CompleteVisitModal
          v-if="showCompleteModal"
          :visit="visitData"
          :services="recordedServices"
          :notes="visitNotes"
          @close="showCompleteModal = false"
          @completed="onVisitCompleted"
      />

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ServiceRecordingModal from "@/components/hew/modals/ServiceRecordingModal.vue";
import CompleteVisitModal from "@/components/hew/modals/CompleteVisitModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "VisitDetails",
  components: {
    NavigationBar,
    ServiceRecordingModal,
    CompleteVisitModal
  },
  data() {
    return {
      // Loading states
      loading: false,

      // Visit data
      visitId: null,
      visitData: null,
      recordedServices: [],
      visitNotes: '',

      // Selected items
      selectedMember: null,
      selectedServiceType: null,

      // UI States
      showServiceModal: false,
      showCompleteModal: false,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    pregnantWomen() {
      return this.visitData?.members.filter(m => this.isPregnant(m)) || [];
    },

    underFiveChildren() {
      return this.visitData?.members.filter(m => this.isChild(m)) || [];
    }
  },

  mounted() {
    this.visitId = sessionStorage.getItem('visitId');
    this.loadVisitDetails();
  },

  methods: {
    async loadVisitDetails() {
      this.loading = true;

      const data = {
        "function": "getVisitDetails",
        "visitId": this.visitId
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.visitData = res.data.data;
          this.recordedServices = res.data.data.services || [];
          this.visitNotes = res.data.data.visit.notes || '';
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load visit details');
        }
      } catch (e) {
        console.error("Failed to load visit details:", e);
        this.showAlert('failed', 'Failed to load visit details');
      } finally {
        this.loading = false;
      }
    },

    recordService(member, serviceType) {
      this.selectedMember = member;
      this.selectedServiceType = serviceType;
      this.showServiceModal = true;
    },

    openServiceModal(serviceType) {
      this.selectedMember = null;
      this.selectedServiceType = serviceType;
      this.showServiceModal = true;
    },

    closeServiceModal() {
      this.showServiceModal = false;
      this.selectedMember = null;
      this.selectedServiceType = null;
    },

    async onServiceRecorded(serviceData) {
      this.closeServiceModal();
      this.showAlert('success', 'Service recorded successfully');

      // Add to recorded services
      this.recordedServices.push(serviceData);

      // Refresh data
      await this.loadVisitDetails();
    },

    async removeService(service) {
      if (!confirm('Are you sure you want to remove this service?')) return;

      const data = {
        "function": "removeService",
        "serviceId": service.service_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Service removed');
          await this.loadVisitDetails();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to remove service');
        }
      } catch (e) {
        console.error("Failed to remove service:", e);
        this.showAlert('failed', 'Failed to remove service');
      }
    },

    async saveNotes() {
      const data = {
        "function": "updateVisitNotes",
        "visitId": this.visitId,
        "notes": this.visitNotes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Notes saved successfully');
        } else {
          this.showAlert('failed', res.data.message || 'Failed to save notes');
        }
      } catch (e) {
        console.error("Failed to save notes:", e);
        this.showAlert('failed', 'Failed to save notes');
      }
    },

    completeVisit() {
      if (this.recordedServices.length === 0) {
        if (!confirm('No services have been recorded. Complete visit anyway?')) {
          return;
        }
      }
      this.showCompleteModal = true;
    },

    async onVisitCompleted() {
      this.showCompleteModal = false;
      this.showAlert('success', 'Visit completed successfully');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        router.push('hew-dashboard');
      }, 2000);
    },

    // Helper methods
    getMemberServices(memberId) {
      return this.recordedServices.filter(s => s.member_id === memberId);
    },

    isChild(member) {
      if (!member.date_of_birth) return false;
      const age = this.getAgeInYears(member.date_of_birth);
      return age < 5;
    },

    isPregnant(member) {
      return member.pregnancy_status === 'ongoing';
    },

    getRequiredVaccinations(member) {
      // This would normally calculate based on age
      const age = this.getAgeInYears(member.date_of_birth);
      if (age < 1) return 8;
      if (age < 2) return 12;
      if (age < 5) return 14;
      return 0;
    },

    getAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageInYears = today.getFullYear() - birthDate.getFullYear();
      const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
          (today.getMonth() - birthDate.getMonth());

      if (ageInMonths < 12) {
        return `${ageInMonths}m`;
      } else if (ageInYears < 2) {
        const months = ageInMonths % 12;
        return `${ageInYears}y ${months}m`;
      } else {
        return `${ageInYears}y`;
      }
    },

    getAgeInYears(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      return today.getFullYear() - birthDate.getFullYear();
    },

    getInitials(firstName, lastName) {
      return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatServiceType(serviceType) {
      if (!serviceType) return '';

      // Convert snake_case to Title Case
      return serviceType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    formatServiceDetails(detailsString) {
      if (!detailsString) return 'No details';

      try {
        const details = JSON.parse(detailsString);
        const parts = [];

        // Service name
        if (details.serviceName) {
          parts.push(details.serviceName);
        }

        // Specific details based on service type
        if (details.topic) {
          parts.push(`Topic: ${this.formatTopic(details.topic)}`);
        }

        if (details.wound_type) {
          parts.push(`Wound: ${this.formatTopic(details.wound_type)}`);
        }

        if (details.treatment) {
          parts.push(`Treatment: ${this.formatTopic(details.treatment)}`);
        }

        if (details.vaccine_name) {
          parts.push(`Vaccine: ${details.vaccine_name}`);
        }

        if (details.dose) {
          parts.push(`Dose: ${details.dose}`);
        }

        // Notes (if any and not empty)
        if (details.notes && details.notes.trim()) {
          parts.push(`Notes: ${details.notes}`);
        }

        // Referral needed
        if (details.referralNeeded) {
          parts.push('⚠️ Referral needed');
        }

        return parts.join(' | ');
      } catch (e) {
        // If parsing fails, return the original string
        return detailsString;
      }
    },

    formatTopic(topic) {
      if (!topic) return '';

      // Convert snake_case to Title Case
      return topic
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },

    getServiceBadgeClass(category) {
      const classes = {
        'preventive': 'bg-primary',
        'clinical': 'bg-danger',
        'education': 'bg-info',
        'maternal': 'bg-warning',
        'child': 'bg-success',
        'referral': 'bg-danger',
        'other': 'bg-secondary'
      };

      return classes[category] || 'bg-secondary';
    },

    // Alert methods
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.dropdown-menu {
  max-height: 400px;
  overflow-y: auto;
}

.dropdown-header {
  color: #495057;
  font-weight: 600;
  padding: 0.5rem 1rem;
  margin-top: 0;
  background-color: #f8f9fa;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.mini-stats-wid .card-body {
  padding: 1.25rem;
}

.service-details {
  max-width: 300px;
  line-height: 1.5;
  font-size: 0.875rem;
}

.alert {
  animation: slideInDown 0.3s ease-out;
}

@keyframes slideInDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Status badges hover effect */
.badge {
  transition: all 0.2s ease;
}

.badge:hover {
  transform: scale(1.05);
}

/* Card hover effects */
.mini-stats-wid {
  transition: all 0.3s ease;
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

/* Dropdown item hover */
.dropdown-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
  padding-left: 1.5rem;
}

/* Avatar styling */
.avatar-xs {
  width: 32px;
  height: 32px;
}

.avatar-sm {
  width: 48px;
  height: 48px;
}
</style>